import React, { useEffect, useContext, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { VsCodeAuthContext } from '../contextApis/vsCodeAuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Github, ArrowRight, Quote, Shield, Lock, KeyRound, X } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import ForgotPasswordModal from '../components/vsCodeAuth/ForgotPasswordModal';
import sixthLogo from '../assets/icon.svg';
import API_ENDPOINTS from '../apiLayer/vsCodeAuthAPIs/authAPIs';
import api from '../API/baseURL';
import { local } from '../utils/constants';
import '../styles/sixthDesign.css';

const VsCodeSignIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('email');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const {
    error,
    loading,
    setLoading,
    setError,
    processSuccessfulAuth,
    handleEmailAuth,
    handleAuth,
    isAuthenticated,
  } = useContext(VsCodeAuthContext);

  // === All auth logic preserved exactly ===

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hasOAuthCode = urlParams.get('code');
    const referrer = urlParams.get('referrer');
    const callbackUrl = urlParams.get('callback_url') || urlParams.get('callback');

    if (hasOAuthCode) return;

    if ((referrer === 'vscode' || callbackUrl) && isAuthenticated()) {
      localStorage.removeItem('is_authenticated');
      localStorage.removeItem('auth_data');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('web_access_token');
      localStorage.removeItem('vscode_jwt_token');
      localStorage.removeItem('api_key');
      localStorage.removeItem('user_email');
      localStorage.removeItem('email');
      return;
    }

    if (isAuthenticated()) {
      navigate('/vscode/dashboard');
    }
  }, [isAuthenticated, navigate, location.search]);

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      handleAuth('google', tokenResponse.code, false);
    },
    onError: () => setError('Google login failed'),
    flow: 'auth-code',
    redirect_uri: local ? 'http://localhost:3000/vscode/signin' : 'https://app.trysixth.com/vscode/signin',
  });

  const handleGitHubLogin = () => {
    const clientId = local ? 'Ov23liJf8RKWHaWd7FM9' : 'Ov23liRQBlkU8uk6vpOG';
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('referrer');
    const callbackUrl = urlParams.get('callback_url') || urlParams.get('callback');
    const teamId = urlParams.get('team_id');

    let redirectUri = local ? 'http://localhost:3000/vscode/signin' : 'https://app.trysixth.com/vscode/signin';
    const params = [];
    if (referrer) params.push(`referrer=${referrer}`);
    if (callbackUrl) params.push(`callback_url=${encodeURIComponent(callbackUrl)}`);
    if (teamId) params.push(`team_id=${teamId}`);
    if (params.length > 0) {
      redirectUri += '?' + params.join('&');
    }

    const scope = 'user:email';
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}`;
    window.location.href = githubAuthUrl;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    const codeParam = searchParams.get('code');
    const referrerParam = searchParams.get('referrer');
    const callbackUrlParam = searchParams.get('callback_url') || searchParams.get('callback');

    if (referrerParam === 'vscode' || referrerParam === 'cli') {
      localStorage.setItem('vscode_referrer', referrerParam);
    } else if (referrerParam === 'teams') {
      localStorage.setItem('vscode_referrer', 'teams');
      const teamIdParam = searchParams.get('team_id');
      if (teamIdParam) localStorage.setItem('pending_team_join_id', teamIdParam);
    } else if (referrerParam === null && !codeParam) {
      localStorage.removeItem('vscode_referrer');
      localStorage.removeItem('vscode_callback_url');
      localStorage.removeItem('pending_team_join_id');
    }

    if (callbackUrlParam && (callbackUrlParam.startsWith('vscode://') || callbackUrlParam.startsWith('http'))) {
      localStorage.setItem('vscode_callback_url', callbackUrlParam);
    }

    if (emailParam) setEmail(emailParam);
    if (codeParam) handleAuth('github', codeParam, false);
  }, [location, handleAuth]);

  const handleEmailCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(API_ENDPOINTS.CHECK_AUTH_PROVIDER, { email });
      if (response.data.has_auth_provider === false) {
        processSuccessfulAuth(response.data, false);
      } else {
        setStep('password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const payload = { email, password, auth_provider: 'email' };
    localStorage.setItem('email', email);
    handleEmailAuth(payload, false);
  };

  // === UI ===

  return (
    <div className="sixth-design sd-page-enter" style={{ minHeight: '100vh' }}>
      <div className="sd-auth-shell" style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '1.05fr 1fr',
      }}>
        {/* Brand panel (left) */}
        <BrandPanel kind="login" />

        {/* Form column (right) */}
        <div style={{
          padding: '60px 44px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <div style={{ marginBottom: 30 }}>
              <h1 style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.025em', margin: '0 0 8px', color: 'var(--text)' }}>
                Sign in
              </h1>
              <p style={{ fontSize: 14.5, color: 'var(--text-2)', margin: 0 }}>
                New here?{' '}
                <Link
                  to={`/vscode/signup${location.search}`}
                  style={{
                    color: 'var(--accent-2)', textDecoration: 'underline', textUnderlineOffset: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Create an account →
                </Link>
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{
                    marginBottom: 16,
                    padding: '10px 14px',
                    borderRadius: 10,
                    background: 'rgba(255,107,107,0.06)',
                    border: '1px solid rgba(255,107,107,0.2)',
                    color: 'var(--danger)',
                    fontSize: 13,
                    textAlign: 'center',
                  }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* OAuth */}
            <OAuthRow onGoogle={() => googleLogin()} onGithub={handleGitHubLogin} disabled={loading} />
            <div style={{ margin: '22px 0' }}>
              <Divider text="or with email" />
            </div>

            <AnimatePresence mode="wait">
              {step === 'email' ? (
                <motion.form
                  key="email-form"
                  onSubmit={handleEmailCheck}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
                >
                  <AuthInput
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      localStorage.setItem('email', e.target.value);
                      setEmail(e.target.value);
                    }}
                    placeholder="you@company.com"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="sd-btn sd-btn-accent"
                    disabled={loading}
                    style={{ height: 46, width: '100%', fontSize: 14.5 }}
                  >
                    {loading ? <span className="sd-spinner" /> : <>Continue <ArrowRight size={14} /></>}
                  </button>
                </motion.form>
              ) : (
                <motion.form
                  key="password-form"
                  onSubmit={handlePasswordSubmit}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
                >
                  <div style={{
                    padding: '10px 14px', borderRadius: 10,
                    border: '1px solid var(--line)',
                    background: 'rgba(255,255,255,0.02)',
                    fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{ color: 'var(--text-2)' }}>{email}</span>
                    <button
                      type="button"
                      onClick={() => { setStep('email'); setError(null); }}
                      style={{
                        background: 'none', border: 0, color: 'var(--accent-2)',
                        fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Change
                    </button>
                  </div>
                  <AuthInput
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoFocus
                    suffix={
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        style={{
                          background: 'none', border: 0, color: 'var(--text-3)',
                          cursor: 'pointer', padding: 6, display: 'inline-flex',
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    }
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: -4 }}>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      style={{
                        background: 'none', border: 0,
                        fontSize: 13, color: 'var(--accent-2)', textDecoration: 'underline', textUnderlineOffset: 3,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <button
                    type="submit"
                    className="sd-btn sd-btn-accent"
                    disabled={loading}
                    style={{ height: 46, width: '100%', fontSize: 14.5 }}
                  >
                    {loading ? <span className="sd-spinner" /> : <>Sign in <ArrowRight size={14} /></>}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>

            <p style={{
              fontSize: 12, color: 'var(--text-4)', textAlign: 'center',
              marginTop: 26, lineHeight: 1.5,
            }}>
              Protected by enterprise-grade encryption.<br />
              By continuing you agree to our{' '}
              <Link to="/terms" style={{ color: 'var(--text-3)', textDecoration: 'underline' }}>Terms</Link>
              {' '}and{' '}
              <Link to="/privacy-policy" style={{ color: 'var(--text-3)', textDecoration: 'underline' }}>Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>

      {showForgotPassword && (
        <div
          className="sixth-design"
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
            padding: 16,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              width: '100%', maxWidth: 440,
              borderRadius: 22, border: '1px solid var(--line)',
              background: 'var(--bg-card)', overflow: 'hidden',
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 22px', borderBottom: '1px solid var(--line)',
            }}>
              <h3 style={{ fontSize: 17, fontWeight: 500, color: 'var(--text)', margin: 0 }}>Forgot password</h3>
              <button
                onClick={() => setShowForgotPassword(false)}
                style={{
                  background: 'none', border: 0, color: 'var(--text-3)',
                  cursor: 'pointer', display: 'inline-flex',
                }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <ForgotPasswordModal />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

/* ───────────────────────── Sub-components (shared between login/signup) ───────────────────────── */

const AuthInput = ({ label, type = 'text', value, onChange, placeholder, suffix, autoFocus, error }) => {
  const [focused, setFocused] = useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <span style={{
        fontSize: 11.5, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        fontWeight: 500,
      }}>{label}</span>
      <div style={{
        position: 'relative',
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid',
        borderColor: error ? 'rgba(255,107,107,0.5)' : (focused ? 'var(--accent-line)' : 'var(--line-2)'),
        borderRadius: 12,
        height: 46,
        display: 'flex', alignItems: 'center',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: focused ? '0 0 0 4px rgba(193,61,255,0.08)' : 'none',
      }}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            background: 'transparent',
            border: 0, outline: 0,
            padding: '0 16px',
            color: 'var(--text)',
            fontSize: 14.5, height: '100%',
            fontFamily: 'inherit',
          }}
        />
        {suffix && <div style={{ paddingRight: 8 }}>{suffix}</div>}
      </div>
      {error && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</span>}
    </label>
  );
};

const OAuthRow = ({ onGoogle, onGithub, disabled }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
    <button
      type="button"
      onClick={onGoogle}
      disabled={disabled}
      className="sd-btn"
      style={{ height: 44, justifyContent: 'center', gap: 9, fontSize: 13.5 }}
    >
      <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Google
    </button>
    <button
      type="button"
      onClick={onGithub}
      disabled={disabled}
      className="sd-btn"
      style={{ height: 44, justifyContent: 'center', gap: 9, fontSize: 13.5 }}
    >
      <Github size={16} />
      GitHub
    </button>
  </div>
);

const Divider = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, color: 'var(--text-3)' }}>
    <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{text}</span>
    <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
  </div>
);

const BrandPanel = ({ kind }) => {
  return (
    <div
      className="sd-auth-brand"
      style={{
        position: 'relative',
        padding: '40px 44px',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between',
        borderRight: '1px solid var(--line)',
        background: 'linear-gradient(180deg, rgba(193,61,255,0.05) 0%, transparent 30%), var(--bg)',
        overflow: 'hidden',
        minHeight: '100%',
      }}
    >
      <div style={{ position: 'relative', zIndex: 2 }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, color: 'var(--text)' }}>
          <img src={sixthLogo} alt="Sixth AI" width={28} height={28} />
          <span style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.02em' }}>Sixth</span>
        </Link>
      </div>

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 460 }}>
        <div style={{
          fontSize: 11, color: 'var(--accent-2)',
          textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 18,
        }}>
          {kind === 'login' ? 'Welcome back' : 'Join Sixth'}
        </div>
        <h2 style={{
          fontSize: 'clamp(34px, 4.4vw, 60px)',
          fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1.0,
          margin: '0 0 20px', color: 'var(--text)',
        }}>
          {kind === 'login' ? (
            <>
              Pick up <span className="sd-serif" style={{ color: 'var(--text-2)', fontWeight: 400 }}>right where</span><br />
              you left off.
            </>
          ) : (
            <>
              The most capable<br />
              <span className="sd-serif" style={{ color: 'var(--text-2)', fontWeight: 400 }}>AI for </span>
              <span style={{
                background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>building.</span>
            </>
          )}
        </h2>
        <p style={{
          fontSize: 15, color: 'var(--text-2)', lineHeight: 1.55,
          margin: '0 0 24px', maxWidth: 380,
        }}>
          {kind === 'login'
            ? 'Sign in to continue your sessions across the editor, Telegram, and the web.'
            : 'Start free — 50 agent requests, no credit card, every frontier model available from day one.'}
        </p>

        <div style={{
          padding: 18,
          borderRadius: 14,
          border: '1px solid var(--line)',
          background: 'rgba(255,255,255,0.02)',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ color: 'var(--accent-line)', marginBottom: 8, display: 'inline-flex' }}>
            <Quote size={16} strokeWidth={1.5} />
          </div>
          <p style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.55, margin: '0 0 12px' }}>
            {kind === 'login'
              ? 'I ship fixes from bed. The Telegram integration is genuinely a game changer.'
              : 'Switched from Cursor last month. Same models, same agent mode, half the price.'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 24, height: 24, borderRadius: 999,
              background: 'rgba(193,61,255,0.18)', color: 'var(--accent-2)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 600, fontSize: 11,
            }}>
              {kind === 'login' ? 'P' : 'D'}
            </span>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {kind === 'login' ? 'Priya R. · Staff Engineer' : 'Daniel C. · Senior Engineer'}
            </div>
          </div>
        </div>
      </div>

      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', flexWrap: 'wrap', gap: '10px 16px',
        fontSize: 12, color: 'var(--text-3)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--success)', display: 'inline-flex' }}><Shield size={13} /></span>
          SOC 2 Type II
        </span>
        <span>·</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--success)', display: 'inline-flex' }}><Lock size={13} /></span>
          Never trained on your code
        </span>
        <span>·</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--success)', display: 'inline-flex' }}><KeyRound size={13} /></span>
          E2E encrypted
        </span>
      </div>

      <div style={{
        position: 'absolute',
        right: -120, bottom: -120,
        width: 480, height: 480,
        opacity: 0.08,
        pointerEvents: 'none',
      }}>
        <img src={sixthLogo} alt="" width={480} height={480} style={{ filter: 'grayscale(1) brightness(0.5)' }} />
      </div>
    </div>
  );
};

export { AuthInput, OAuthRow, Divider, BrandPanel };
export default VsCodeSignIn;
